import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch, FiShoppingCart, FiMenu, FiX } from 'react-icons/fi';
import { useCart } from '../context/CartContext';

const HeaderContainer = styled.header`
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const TopBar = styled.div`
  background: #1a1a1a;
  color: white;
  padding: 8px 0;
  font-size: 14px;
  text-align: center;
`;

const MainHeader = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Logo = styled(Link)`
  font-size: 2rem;
  font-weight: bold;
  color: #1a1a1a;
  text-decoration: none;
  
  &:hover {
    color: #333;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 500px;
  position: relative;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 20px 12px 50px;
  border: 2px solid #e0e0e0;
  border-radius: 25px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s;

  &:focus {
    border-color: #1a1a1a;
  }

  &::placeholder {
    color: #999;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const CartButton = styled(Link)`
  position: relative;
  color: #1a1a1a;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #1a1a1a;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Navigation = styled.nav<{ isOpen: boolean }>`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    padding: 1rem;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 1rem 0;
  gap: 2rem;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 0;
  }
`;

const NavItem = styled.li``;

const NavLink = styled(Link)`
  color: #1a1a1a;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const MobileSearchContainer = styled.div`
  display: none;
  padding: 1rem;
  border-top: 1px solid #e0e0e0;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cart } = useCart();
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsMobileMenuOpen(false);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <HeaderContainer>
      <TopBar>
        Ücretsiz Kargo - 500 TL ve Üzeri Alışverişlerde
      </TopBar>
      
      <MainHeader>
        <Logo to="/">Fashion Connect</Logo>
        
        <SearchContainer>
          <form onSubmit={handleSearch}>
            <SearchIconWrapper>
              <FiSearch size={20} />
            </SearchIconWrapper>
            <SearchInput
              type="text"
              placeholder="Ürün, marka veya kategori ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        </SearchContainer>

        <NavActions>
          <CartButton to="/cart">
            <FiShoppingCart size={24} />
            {cart.itemCount > 0 && (
              <CartBadge>{cart.itemCount}</CartBadge>
            )}
            <span>Sepet</span>
          </CartButton>
          
          <MobileMenuButton onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </MobileMenuButton>
        </NavActions>
      </MainHeader>

      <Navigation isOpen={isMobileMenuOpen}>
        <NavList>
          <NavItem>
            <NavLink to="/category/takim-elbise" onClick={() => setIsMobileMenuOpen(false)}>
              Takım Elbise
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/category/gomlek" onClick={() => setIsMobileMenuOpen(false)}>
              Gömlek
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/category/pantolon" onClick={() => setIsMobileMenuOpen(false)}>
              Pantolon
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/category/tisort" onClick={() => setIsMobileMenuOpen(false)}>
              Tişört
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/category/ayakkabi" onClick={() => setIsMobileMenuOpen(false)}>
              Ayakkabı
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/category/aksesuar" onClick={() => setIsMobileMenuOpen(false)}>
              Aksesuar
            </NavLink>
          </NavItem>
        </NavList>
        
        <MobileSearchContainer>
          <form onSubmit={handleSearch}>
            <SearchInput
              type="text"
              placeholder="Ürün, marka veya kategori ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        </MobileSearchContainer>
      </Navigation>
    </HeaderContainer>
  );
};

export default Header;
